import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import { usersApi } from '@/api/users.api';
import { AppShell } from '@/components/layout/AppShell';

const ROW_KEYS = [
  'email_envelope_sent',
  'email_envelope_signed',
  'email_envelope_completed',
  'email_envelope_declined',
  'email_envelope_reminded',
  'email_envelope_voided',
] as const;

export function NotificationsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: prefs } = useQuery({
    queryKey: ['users', 'me', 'notifications'],
    queryFn: () => usersApi.getNotificationPrefs(),
  });
  const [local, setLocal] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (prefs) setLocal(prefs);
  }, [prefs]);

  const saveMutation = useMutation({
    mutationFn: () => usersApi.updateNotificationPrefs(local),
    onSuccess: () => {
      toast.success(t('settings_notifications.saved'));
      qc.invalidateQueries({ queryKey: ['users', 'me', 'notifications'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const dirty = prefs && ROW_KEYS.some((k) => !!prefs[k] !== !!local[k]);

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title">{t('settings_notifications.title')}</h1>
          <p className="page-subtitle">{t('settings_notifications.subtitle')}</p>
        </div>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={!dirty || saveMutation.isPending}
          className="btn btn-primary"
        >
          {saveMutation.isPending ? t('common.saving') : t('settings_notifications.save_btn')}
        </button>
      </div>

      <div className="card !p-0">
        <ul className="divide-y divide-border">
          {ROW_KEYS.map((key) => (
            <li key={key} className="px-4 py-3 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-gray-900">{t(`settings_notifications.${key}_title`)}</div>
                <div className="text-[12px] text-muted">{t(`settings_notifications.${key}_desc`)}</div>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={local[key] ?? true}
                  onChange={(e) =>
                    setLocal((prev) => ({ ...prev, [key]: e.target.checked }))
                  }
                  className="sr-only peer"
                />
                <span className="h-5 w-9 rounded-full bg-gray-300 peer-checked:bg-primary transition-colors" />
                <span
                  className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4"
                />
              </label>
            </li>
          ))}
        </ul>
      </div>
    </AppShell>
  );
}
